import { useEffect, useState } from "react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import api from "@/lib/api";
import { money } from "@/components/admin/AdminLayout";

const COLORS = ["#2563EB", "#16A34A", "#F59E0B", "#DC2626", "#7C3AED"];

const Stat = ({ label, value }) => (
  <div className="bg-white border border-zinc-200 rounded-xl p-5"><p className="text-xs text-zinc-500">{label}</p><p className="text-xl font-bold mt-1 font-display">{value}</p></div>
);

export default function Analytics() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="animate-pulse h-96 bg-zinc-100 rounded-xl" />;
  const t = d.totals;

  return (
    <div className="space-y-6">
      <div><h1 className="font-display text-2xl font-bold tracking-tight">Analytics</h1><p className="text-sm text-zinc-500">Revenue, profit & performance</p></div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Revenue" value={money(t.revenue)} />
        <Stat label="Profit" value={money(t.profit)} />
        <Stat label="Supplier Costs" value={money(t.supplier_costs)} />
        <Stat label="Payment Fees" value={money(t.payment_fees)} />
        <Stat label="Orders" value={t.orders} />
        <Stat label="Avg Order Value" value={money(t.aov)} />
        <Stat label="Shipping Costs" value={money(t.shipping_costs)} />
        <Stat label="Refunds" value={t.refunds} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-xl p-5">
          <h3 className="font-semibold text-sm mb-4">Revenue & Orders Over Time</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={d.orders_over_time}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(x) => x.slice(5)} stroke="#a1a1aa" />
              <YAxis tick={{ fontSize: 10 }} stroke="#a1a1aa" />
              <Tooltip />
              <Line type="monotone" dataKey="revenue" stroke="#2563EB" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="orders" stroke="#16A34A" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-zinc-200 rounded-xl p-5">
          <h3 className="font-semibold text-sm mb-4">Revenue by Category</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={d.top_categories} dataKey="revenue" nameKey="category" cx="50%" cy="50%" outerRadius={90}>
                {d.top_categories.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => money(v)} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-zinc-200 rounded-xl p-5">
          <h3 className="font-semibold text-sm mb-4">Best Sellers</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={d.best_sellers} layout="vertical" margin={{ left: 20 }}>
              <XAxis type="number" tick={{ fontSize: 10 }} stroke="#a1a1aa" />
              <YAxis type="category" dataKey="title" tick={{ fontSize: 9 }} width={120} stroke="#a1a1aa" />
              <Tooltip formatter={(v) => money(v)} />
              <Bar dataKey="revenue" fill="#2563EB" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="grid grid-cols-2 gap-6">
          <TopList title="Top Countries" items={d.top_countries} labelKey="country" />
          <TopList title="Top Suppliers" items={d.top_suppliers} labelKey="supplier" />
        </div>
      </div>
    </div>
  );
}

const TopList = ({ title, items, labelKey }) => (
  <div className="bg-white border border-zinc-200 rounded-xl p-5">
    <h3 className="font-semibold text-sm mb-3">{title}</h3>
    <div className="space-y-2">
      {items.length === 0 && <p className="text-xs text-zinc-400">No data</p>}
      {items.map((i, idx) => (
        <div key={idx} className="flex justify-between text-sm"><span className="text-zinc-600 capitalize">{i[labelKey]}</span><span className="font-medium">{money(i.revenue)}</span></div>
      ))}
    </div>
  </div>
);
